// The home page (Aug 2026): where the logo lands. A player's front door,
// not a dashboard — pick up where you left off, reach for a recent tune, or
// jump to a workspace. Recents are PlaylistTuneRef entries recorded by the
// ONE load seam in tuneSources (palette, search and this page all record the
// same way), so a card here resolves exactly like a playlist row and can never
// load a different thing than it names.
//
// The tune search sits at the top (Oct 2026: the Tunes page folded in, user:
// "a search bar at the top and all the sections below"); a search shows its
// results in place of the sections. Nothing here names the collections the
// tunes come from (user ruling, Oct 2026).

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '@/navigation';
import {
  continueTuneWorkspaceHref,
  lastTuneWorkspace,
  tuneWorkspaceKey,
} from '@/components/tuneWorkspace';
import { useHistoryView, useRecentsView } from '@/state/historyView';
import { useStore } from '@/state/store';
import { usePlaylists } from '@/data/playlistAccess';
import {
  lovedRows,
  mostPlayedRows,
  recentTuneRows,
  tuneSubtitle,
  tuneVersionLabel,
  type TuneSourceEntry,
} from '@/data/tuneSources';
import { playedLabel } from '@/state/tuneHistory';
import type { TasteTune } from '@/state/tuneTaste';
import { libraryTuneMatcher } from '@/music/libraryIdentity';
import { useLibraryStats } from '@/data/libraryStats';
import { openbookLibrary, type OpenbookLibraryEntry } from '@/data/openbookLibrary';
import { realbookLibrary, type RealbookLibraryEntry } from '@/data/realbookLibrary';
import { useLibraryTunes } from '@/data/libraryTunes';
import { addedLabel, recentlyAddedTunes } from '@/data/recentlyAdded';
import { LibraryStats } from '@/components/LibraryStats';
import { TuneSearch } from '@/components/TuneSearch';
import { TopPicks } from '@/components/TopPicks';
import { MoreLikeShelves } from '@/components/MoreLikeShelves';
import { HomeTile } from '@/components/HomeTile';
import { HomeNoticeToast } from '@/components/HomeNoticeToast';
import { tuneHref } from '@/components/tuneHref';

/** A row as Love marks it: by the version you opened. */
function rowTasteTune(row: TuneSourceEntry): TasteTune {
  return {
    title: row.title,
    ...(row.composer ? { composer: row.composer } : {}),
    ...(row.ref ? { ref: row.ref } : {}),
  };
}

/** A tune card on the Recent tunes and Most played shelves. */
function TuneCard({
  row,
  meta,
  onOpen,
  testId,
}: {
  row: TuneSourceEntry;
  meta: string;
  onOpen: (row: TuneSourceEntry) => void;
  testId?: string;
}) {
  return (
    <HomeTile
      kind="recent"
      testId={testId}
      title={row.title}
      melody={row.melody}
      subtitle={tuneSubtitle(row)}
      foot={meta}
      onOpen={() => onOpen(row)}
      taste={rowTasteTune(row)}
    />
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const chart = useStore((s) => s.chart);
  const scoreId = useStore((s) => tuneWorkspaceKey(s.score));
  const recentTunes = useRecentsView();
  const tuneHistory = useHistoryView();
  const tuneTaste = useStore((s) => s.tuneTaste);
  const savedCharts = useStore((s) => s.savedCharts);
  const userPlaylists = usePlaylists();
  const [openbook, setOpenbook] = useState<OpenbookLibraryEntry[]>([]);
  const [realbook, setRealbook] = useState<RealbookLibraryEntry[]>([]);
  // Always: the Recently added shelf lists the library's new tunes.
  const library = useLibraryTunes();
  const stats = useLibraryStats();
  const findLibraryTune = useMemo(
    () =>
      stats && libraryTuneMatcher(stats.tunes.map((tune) => ({ title: tune.t, composer: tune.c }))),
    [stats],
  );
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    void openbookLibrary().then(setOpenbook);
    void realbookLibrary().then(setRealbook);
  }, []);

  const rows = useMemo(
    () =>
      recentTuneRows({
        presets: [],
        savedCharts,
        openbook,
        realbook,
        library,
        userPlaylists,
        playlist: null,
      }),
    [recentTunes, savedCharts, openbook, realbook, library, userPlaylists], // eslint-disable-line react-hooks/exhaustive-deps -- rows read recentTunes via the store inside recentTuneRows
  );
  const played = useMemo(
    () =>
      mostPlayedRows({
        presets: [],
        savedCharts,
        openbook,
        realbook,
        library,
        userPlaylists,
        playlist: null,
      }),
    [tuneHistory, savedCharts, openbook, realbook, library, userPlaylists], // eslint-disable-line react-hooks/exhaustive-deps -- rows read tuneHistory via the store inside mostPlayedRows
  );
  const loved = useMemo(
    () =>
      lovedRows({
        presets: [],
        savedCharts,
        openbook,
        realbook,
        library,
        userPlaylists,
        playlist: null,
      }),
    [tuneTaste, savedCharts, openbook, realbook, library, userPlaylists], // eslint-disable-line react-hooks/exhaustive-deps -- rows read tuneTaste via the store inside lovedRows
  );
  const added = useMemo(() => recentlyAddedTunes({ library, now: Date.now() }), [library]);
  // A tune you love joins the front of the Loved row: bring the row back to
  // its start, or on a phone the new tile sits off its left edge.
  const lovedRow = useRef<HTMLUListElement>(null);
  const newestLoved = loved[0] ? `${loved[0].mark.title}|${loved[0].mark.at}` : '';
  useLayoutEffect(() => {
    lovedRow.current?.scrollTo?.({ left: 0 });
  }, [newestLoved]);

  const hasCurrentTune = chart.title.trim() !== '' && chart.title !== 'Untitled Chart';
  const continuedMode = lastTuneWorkspace(scoreId)?.mode;

  // Root-level deep links predate the home page: '/' used to redirect into
  // the workbench WITH its query, so `/?tune=…` links must keep resolving.
  // Forward them rather than silently showing the shelf.
  const params = new URLSearchParams(location.search);
  if (params.get('tune') || params.get('score')) {
    return <Navigate replace to={{ pathname: APP_ROUTES.workbench, search: location.search }} />;
  }

  const open = async (row: TuneSourceEntry) => {
    setFailed(null);
    try {
      await row.load();
      navigate(APP_ROUTES.workbench);
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Could not load that tune.');
    }
  };

  return (
    <div className="home-page" data-testid="home-page">
      <h1 className="visually-hidden">Home</h1>
      <TuneSearch>
        <section className="home-hero">
          <div className="home-actions">
            {hasCurrentTune ? (
              <Link
                className="btn btn-primary"
                to={continueTuneWorkspaceHref(scoreId)}
                data-testid="home-continue"
              >
                {continuedMode === 'edit'
                  ? 'Continue editing'
                  : continuedMode === 'arrange'
                    ? 'Continue arranging'
                    : 'Continue'}{' '}
                · {chart.title}
              </Link>
            ) : (
              <Link className="btn btn-primary" to={APP_ROUTES.workbench}>
                Open the workbench
              </Link>
            )}
            <Link className="btn" to={APP_ROUTES.playlists}>
              Playlists
            </Link>
            <Link className="btn" to={APP_ROUTES.chordLibrary}>
              Chord library
            </Link>
            <Link className="btn" to={APP_ROUTES.learn}>
              Learn
            </Link>
          </div>
        </section>

        {added.length > 0 && (
          <section
            className="home-shelf"
            aria-labelledby="home-added-heading"
            data-testid="home-recently-added"
            data-scroll-anchor="home-recently-added"
          >
            <h2 id="home-added-heading">Recently added</h2>
            <ul className="home-shelf-row">
              {added.map(({ row, addedAt }) => (
                <li key={row.key}>
                  <HomeTile
                    kind="shelf"
                    testId="home-added-tune"
                    reason={`${addedLabel(addedAt)} · ${tuneVersionLabel(row.source)}`}
                    title={row.title}
                    melody={row.melody}
                    foot={row.composer}
                    onOpen={() => void open(row)}
                    taste={rowTasteTune(row)}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        <TopPicks />

        <section
          className="home-recents"
          aria-label="Recent tunes"
          data-scroll-anchor="home-recents"
        >
          <h2>Recent tunes</h2>
          {failed && (
            <p className="home-load-error" role="alert">
              {failed}
            </p>
          )}
          {rows.length === 0 ? (
            <p className="muted">
              Tunes you open appear here. Search above or use <kbd>⌘K</kbd>.
            </p>
          ) : (
            <ul className="home-recent-grid">
              {rows.map((row) => (
                <li key={row.key}>
                  <TuneCard
                    row={row}
                    meta={[tuneVersionLabel(row.source), row.musicKey, row.style]
                      .filter(Boolean)
                      .join(' · ')}
                    onOpen={(tune) => void open(tune)}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        {played.length > 0 && (
          <section
            className="home-recents"
            aria-label="Most played"
            data-testid="home-most-played"
            data-scroll-anchor="home-most-played"
          >
            <h2>Most played</h2>
            <ul className="home-recent-grid">
              {played.map(({ row, playSeconds }) => (
                <li key={row.key}>
                  <TuneCard
                    row={row}
                    meta={[tuneVersionLabel(row.source), playedLabel(playSeconds)].join(' · ')}
                    onOpen={(tune) => void open(tune)}
                    testId="home-most-played-tune"
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        {loved.length > 0 && (
          <section
            className="home-shelf"
            aria-labelledby="home-loved-heading"
            data-testid="home-loved"
            data-scroll-anchor="home-loved"
          >
            <h2 id="home-loved-heading">Loved</h2>
            <ul className="home-shelf-row" ref={lovedRow}>
              {loved.map(({ mark, row }) => {
                // A heart from a Top pick names the library's tune: its link
                // opens it, and the library says whether it has a melody.
                const libraryTune = !row && findLibraryTune?.(mark.title, mark.composer);
                const melody = row
                  ? row.melody
                  : typeof libraryTune === 'number' && !!stats?.tunes[libraryTune].m;
                return (
                  <li key={`${mark.title}|${mark.composer ?? ''}`}>
                    <HomeTile
                      kind="shelf"
                      testId="home-loved-tune"
                      title={mark.title}
                      melody={melody}
                      foot={mark.composer}
                      {...(row
                        ? { onOpen: () => void open(row) }
                        : { to: tuneHref(mark.title, mark.composer, mark.source) })}
                      taste={mark}
                      hides="unlove"
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <MoreLikeShelves />

        {userPlaylists.length > 0 && (
          <section
            className="home-playlists"
            aria-label="Playlists"
            data-scroll-anchor="home-playlists"
          >
            <h2>
              <Link to={APP_ROUTES.playlists}>Playlists</Link>
            </h2>
            <ul className="home-playlist-row">
              {userPlaylists.map((p) => (
                <li key={p.id}>
                  <Link className="home-playlist-chip" to={`${APP_ROUTES.playlists}/${p.id}`}>
                    {p.name} <span className="muted">· {p.entries.length}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <LibraryStats />
      </TuneSearch>
      <HomeNoticeToast />
    </div>
  );
}
